/**
 * Shared notification stacking layer.
 *
 * Theme Modal overlays sit at `z-[100000]` and several product modals go much
 * higher (events-review `999999`, banner `9999999`, capture `99999999`). A toast
 * at `100100` still loses to those, and Chrome can composite a later
 * `backdrop-filter` overlay on top of an earlier sibling even when the sibling
 * has a slightly higher z-index.
 *
 * This layer is isolated, compositor-promoted, and kept as the last child of
 * `document.body` (via MutationObserver) so Enrich / idle-timeout / upload
 * toasts stay readable over later modal portals.
 */
export const TOAST_Z_INDEX = 100000000;
export const TOAST_Z_CLASS = 'z-[100000000]';
export const TOAST_LAYER_ID = 'tp-toast-layer';

let watchers = 0;
let observer: MutationObserver | null = null;
let watchedLayer: HTMLElement | null = null;
let movingLayer = false;

function applyToastLayerStyle(layer: HTMLElement): void {
  layer.style.position = 'fixed';
  layer.style.inset = '0';
  layer.style.zIndex = String(TOAST_Z_INDEX);
  layer.style.pointerEvents = 'none';
  layer.style.isolation = 'isolate';
  // Promote a compositor layer so modal `backdrop-filter` cannot paint over us.
  layer.style.transform = 'translateZ(0)';
}

function moveLayerLast(layer: HTMLElement): void {
  if (movingLayer || document.body.lastElementChild === layer) return;
  movingLayer = true;
  document.body.appendChild(layer);
  movingLayer = false;
}

function startObserver(layer: HTMLElement): void {
  watchedLayer = layer;
  if (observer) return;
  observer = new MutationObserver(() => {
    if (watchedLayer) moveLayerLast(watchedLayer);
  });
  observer.observe(document.body, { childList: true });
}

function stopObserver(): void {
  observer?.disconnect();
  observer = null;
  watchedLayer = null;
}

/** Create (or reuse) the body-level toast layer and move it after modal portals. */
export function ensureToastLayer(): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  let layer = document.getElementById(TOAST_LAYER_ID);
  if (!layer) {
    layer = document.createElement('div');
    layer.id = TOAST_LAYER_ID;
    applyToastLayerStyle(layer);
    document.body.appendChild(layer);
    return layer;
  }
  applyToastLayerStyle(layer);
  moveLayerLast(layer);
  return layer;
}

/**
 * Retain the toast layer and keep it after later `document.body` portals for the
 * lifetime of the caller. Pair with {@link unwatchToastLayer} on unmount.
 */
export function watchToastLayer(): HTMLElement | null {
  const layer = ensureToastLayer();
  if (!layer) return null;
  watchers += 1;
  startObserver(layer);
  moveLayerLast(layer);
  return layer;
}

export function unwatchToastLayer(): void {
  watchers = Math.max(0, watchers - 1);
  if (watchers === 0) stopObserver();
}
